import { defineStore } from 'pinia';
import { useUserStore } from '@/stores/user';
import { UserRole } from '~/config/user-roles';
import {
  useApiNotification,
  type UserNotificationResponseItem,
} from '@/composables/api-services/api-notfication';

export const defaultUserNotificationSize = 10;

export const useUserNotificationsStore = defineStore('userNotifications', {
  state: () => {
    return {
      isLoading: false,
      userNotifications: [] as UserNotificationResponseItem[],
      unread: 0,
      totalElements: 0,
      isUpdateLoading: false,
    };
  },

  getters: {
    getIsLoading: (state) => {
      return state.isLoading;
    },
    getUserNotifications: (state) => {
      return state.userNotifications;
    },
  },
  actions: {
    setIsLoading(isLoading: boolean) {
      this.isLoading = isLoading;
    },
    async fetch(input: { page?: number; size?: number }) {
      const userStore = useUserStore();

      try {
        if (
          this.isLoading ||
          !userStore.user ||
          userStore.user.role === UserRole.CLIENT
        ) {
          return;
        }
        this.isLoading = true;
        const response = await fetchUserNotifications({
          page: input.page ?? 1,
          size: input.size ?? defaultUserNotificationSize,
        });
        if (response) {
          this.userNotifications = response.items;
          this.unread = response.unread;
          this.totalElements = response.totalElements;
        }
      } catch (error) {
        useErrorHandler(error);
      } finally {
        this.isLoading = false;
      }
    },
    async updateNotificationViewedStatus(ids: string[], isViewed: boolean) {
      const apiNotifications = useApiNotification();

      if (this.isUpdateLoading) {
        return;
      }

      this.isUpdateLoading = true;

      try {
        await apiNotifications.updateNotificationViewedStatus({
          notificationIds: ids,
          isViewed,
        });
      } catch (error) {
        useErrorHandler(error);
      } finally {
        this.isUpdateLoading = false;
      }
    },
  },
});

export const fetchUserNotifications = async ({
  page,
  size,
}: {
  page?: number;
  size?: number;
}) => {
  const apiNotifications = useApiNotification();
  return await apiNotifications.getNotifications({ page, size });
};
